import cx from 'classnames';
import type { FC, SyntheticEvent } from 'react';
import { useState } from 'react';
import Dropzone from 'react-dropzone';

import useTracking from '@zen/utils/hooks/useTracking';
import { createTrackingEvent, createTrackingLabelAttribute, createTrackingParentAttribute } from '@zen/utils/tracking';

import Button from '../Button';
import Icon from '../Icon';

interface Props {
  className?: string;
  errorMessage?: string;
  isDisabled?: boolean;
  onDrop: (files: File[]) => void;
  tagline?: string;
}

export const displayName = 'Design System/DropZone';

const DropZone: FC<Props> = (props) => {
  const { onDrop, className: dropZoneClassName, errorMessage, isDisabled, tagline } = props;

  const [isActive, setIsActive] = useState<boolean>(false);
  const { trackEvent } = useTracking();

  const classNames: string = cx(
    'flex items-center justify-center',
    'rounded-sm border-dashed',
    'px-6',
    'hover:shadow-fixed-top',
    'cursor-pointer transition-all duration-200 ease-linear',
    {
      'border-2 border-azure-light': isActive,
      'border border-grey-lighter': !isActive && !errorMessage,
      'border border-red-dark': !isActive && errorMessage
    },
    dropZoneClassName
  );

  const handleDrop = (acceptedFiles: File[]): void => {
    setIsActive(false);

    onDrop(acceptedFiles);
  };

  const trackDropzoneEvent = (event: SyntheticEvent): void => {
    const trackingEvent = createTrackingEvent(displayName, event.type, event);

    trackEvent(trackingEvent);
  };

  return (
    <div>
      <Dropzone
        disabled={isDisabled}
        onDragEnter={() => {
          setIsActive(true);
        }}
        onDragLeave={() => {
          setIsActive(false);
        }}
        onDrop={handleDrop}
      >
        {({ getRootProps, getInputProps }) => (
          <div
            {...getRootProps({
              className: classNames,
              'data-component': 'dropzone',
              'data-testid': 'dropzone',
              onClick: trackDropzoneEvent,
              onDrop: trackDropzoneEvent,
              ...createTrackingParentAttribute('dropzone')
            })}
          >
            <input {...getInputProps()} />
            <div className="flex space-x-4 py-10 text-center items-center" {...createTrackingLabelAttribute('dropzone')}>
              <Icon className="text-base" icon="zicon-upload" />
              <div className="text-sm text-grey-dark">Drag and drop your files here to upload or</div>
              <Button variant="secondary">Browse files</Button>
            </div>
          </div>
        )}
      </Dropzone>
      {errorMessage && <div className="text-sm text-red-dark mt-2">{errorMessage}</div>}
      {tagline && !errorMessage && <div className="text-sm text-grey-base mt-2">{tagline}</div>}
    </div>
  );
};

export type { Props as DropZoneProps };

export default DropZone;
